import { useCallback, useEffect, useState } from 'react';
import { LscApiError, getLscLeagueReadiness } from '../api';
import type { LscLeagueReadiness } from '../types';

export type LeagueReadinessAuthError = 'unauthorized' | 'forbidden';

export interface UseLeagueReadinessResult {
  readiness: LscLeagueReadiness | null;
  loading: boolean;
  error: string | null;
  authError: LeagueReadinessAuthError | null;
  reload: () => void;
}

export function useLeagueReadiness(leagueId: string | undefined): UseLeagueReadinessResult {
  const [readiness, setReadiness] = useState<LscLeagueReadiness | null>(null);
  const [loading, setLoading] = useState(Boolean(leagueId));
  const [error, setError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<LeagueReadinessAuthError | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => {
    setReloadToken((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!leagueId) {
      setReadiness(null);
      setLoading(false);
      setError(null);
      setAuthError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setAuthError(null);

    getLscLeagueReadiness(leagueId)
      .then((data) => {
        if (cancelled) return;
        setReadiness(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setReadiness(null);
        if (err instanceof LscApiError) {
          if (err.status === 401) {
            setAuthError('unauthorized');
            return;
          }
          if (err.status === 403) {
            setAuthError('forbidden');
            return;
          }
        }
        setError(
          err instanceof Error
            ? err.message
            : 'Unable to load setup status. Try again in a moment.'
        );
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [leagueId, reloadToken]);

  return {
    readiness,
    loading,
    error,
    authError,
    reload,
  };
}
